import React, { forwardRef } from 'react';
import styled from 'styled-components';

interface TimePickerProps {
  label?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
}

const TimePickerContainer = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
`;

const Label = styled.label<{ $error: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$error ? 'var(--destructive)' : 'var(--foreground)'};
`;

const TimeInput = styled.input<{
  $error: boolean;
  $disabled: boolean;
  $size: string;
  $fullWidth: boolean;
}>`
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  padding: ${props => {
    switch (props.$size) {
      case 'sm': return '0.375rem 0.75rem';
      case 'lg': return '0.75rem 1rem';
      default: return '0.5rem 0.875rem';
    }
  }};
  font-size: ${props => {
    switch (props.$size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1rem';
      default: return '0.875rem';
    }
  }};
  background: var(--background);
  border: 1px solid ${props => props.$error ? 'var(--destructive)' : 'var(--border)'};
  border-radius: var(--radius);
  color: var(--foreground);
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    border-color: var(--ring);
  }
  
  &:focus {
    outline: none;
    border-color: var(--ring);
    box-shadow: 0 0 0 2px var(--ring);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: var(--calendar-picker-filter, none);
  }
`;

const HelperText = styled.span<{ $error: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$error ? 'var(--destructive)' : 'var(--muted-foreground)'};
`;

const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  ({
    label,
    value,
    onChange,
    placeholder,
    disabled = false,
    error = false,
    helperText,
    size = 'md',
    fullWidth = false,
    required = false,
    name,
    id
  }, ref) => {
    return (
      <TimePickerContainer $fullWidth={fullWidth}>
        {label && (
          <Label $error={error}>
            {label}
            {required && <span style={{ color: 'var(--destructive)' }}> *</span>}
          </Label>
        )}
        
        <TimeInput
          ref={ref}
          type="time"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          $error={error}
          $disabled={disabled}
          $size={size}
          $fullWidth={fullWidth}
          name={name}
          id={id}
        />
        
        {helperText && (
          <HelperText $error={error}>
            {helperText}
          </HelperText>
        )}
      </TimePickerContainer>
    );
  }
);

TimePicker.displayName = 'TimePicker';

export default TimePicker; 